import {AggregationPage} from "crqlar-react-common/dist/models/aggregation/aggregation.model";
import api from "crqlar-react-common/dist/services/api";
import {
    DigitalSigningTemplate,
    DigitalSigningTemplateInstantiateRequest,
    DigitalSigningTemplateListRequest,
    DigitalSigningTemplateListRow,
    DigitalSigningTemplateSaveRequest,
    DigitalSigningTemplateStarter,
    DigitalSigningTipServices
} from "./digitalSigningTemplate.model";

export class DigitalSigningTemplateService {

    private static URI = "/v3/digitalSigningTemplate";
    private static AGGREGATION_URI = "/v3/aggregation/digitalSigning/templates";
    private static TIP_SERVICES_URI = "/v3/digitalSigning/tipServices";

    /** Always the effective values; 404 when it is unknown or archived. */
    static async get(id: string): Promise<DigitalSigningTemplate> {
        return (await api.post<DigitalSigningTemplate>(`${DigitalSigningTemplateService.URI}/get`, {id})).data;
    }

    static async add(request: DigitalSigningTemplateSaveRequest): Promise<DigitalSigningTemplate> {
        return (await api.post<DigitalSigningTemplate>(`${DigitalSigningTemplateService.URI}/add`, request)).data;
    }

    /** Applies to the next issued link; an issued one keeps its frozen snapshot. */
    static async update(request: DigitalSigningTemplateSaveRequest): Promise<DigitalSigningTemplate> {
        return (await api.post<DigitalSigningTemplate>(`${DigitalSigningTemplateService.URI}/update`, request)).data;
    }

    /** A sector's tip services, set on the services: shared by every template offered in the sector. */
    static async tipServices(sectorId: string): Promise<DigitalSigningTipServices> {
        return (await api.post<DigitalSigningTipServices>(`${DigitalSigningTemplateService.TIP_SERVICES_URI}/get`, {sectorId})).data;
    }

    /** Archives them; the links issued with them keep their pages. */
    static async delete(ids: string[]): Promise<void> {
        await api.post(`${DigitalSigningTemplateService.URI}/delete`, {id: ids});
    }

    /** The sectors some template is scoped to: where the folio dialog offers digital signing. */
    static async offeredSectorIds(): Promise<string[]> {
        const response = await api.post<{sectorIds?: string[] | null}>(`${DigitalSigningTemplateService.URI}/offered`, {});
        return response.data.sectorIds ?? [];
    }

    /** The list page's rows, sortable by `DigitalSigningTemplateListSortField`. */
    static async listRows(request: DigitalSigningTemplateListRequest): Promise<AggregationPage<DigitalSigningTemplateListRow>> {
        return (await api.post<AggregationPage<DigitalSigningTemplateListRow>>(
            `${DigitalSigningTemplateService.AGGREGATION_URI}/list`,
            request
        )).data;
    }

    /** The starter catalog: picker labels in [language], each with the draft it would create. */
    static async starters(language?: string): Promise<DigitalSigningTemplateStarter[]> {
        return (await api.post<DigitalSigningTemplateStarter[]>(`${DigitalSigningTemplateService.URI}/templates`, {language})).data;
    }

    /** The blank starter, unsaved and unnamed - the caller names it and picks its sectors. */
    static async blank(): Promise<DigitalSigningTemplate> {
        return (await api.post<DigitalSigningTemplate>(`${DigitalSigningTemplateService.URI}/templates/blank`)).data;
    }

    /** Creates the template a starter stands for and returns it saved. */
    static async instantiate(request: DigitalSigningTemplateInstantiateRequest): Promise<DigitalSigningTemplate> {
        return (await api.post<DigitalSigningTemplate>(`${DigitalSigningTemplateService.URI}/templates/instantiate`, request)).data;
    }
}
